import { create } from 'zustand';
import { router } from 'expo-router';
import { nanoid } from 'nanoid/non-secure';
import { createBuild, getBuild } from '@/llm/buildsClient';
import { buildInputsFromStore } from '@/onboarding/buildInputs';
import { finalizeBuild } from '@/onboarding/finalizeBuild';
import { useOnboardingStore } from '@/store/onboardingStore';
import { routeDenied } from '@/llm/denyRouting';
import { mmkv } from '@/storage/mmkv';
import { track } from '@/analytics/track';
import { useProjectStore } from '@/store/projectStore';
import { WebAppGenerationError } from '@/webapp/appCode';
import type { AppPlan } from '@/webapp/plan/planTypes';
import type { SelectedPlan } from '@/llm/planTypes';

// Owns the app-build lifecycle OUTSIDE any screen, so a build keeps running when the
// user navigates away from the onboarding wizard (the screen can unmount freely). A
// global BuildProgressOverlay renders from this state, and on success we reset the
// nav stack and land in the editor — never stacked on top of the onboarding flow.

type Status = 'idle' | 'running' | 'error';

interface ActiveBuild {
  buildId: string;
  appName: string;
  goal: string;
  plan: SelectedPlan | null;
  signedIn: boolean;
}

interface BuildRunState {
  status: Status;
  /** Server-reported build phase (e.g. 'queued', 'generating', 'enriching', 'done'). */
  phase: string;
  /** Snapshot of the chosen plan (for the wireframe in the overlay); null for non-plan builds. */
  plan: SelectedPlan | null;
  error: string | null;
  start: (signedIn: boolean) => void;
  retry: () => void;
  dismiss: () => void;
  resume: () => void;
}

const POLL_MS = 3000;
const MMKV_KEY = 'activeBuild';

let pollTimer: ReturnType<typeof setInterval> | null = null;
let lastSignedIn = true;
let buildStartedAt = 0;
let buildFirstBuild = false;
// A mini-app (or translate) build runs in-process with nothing to clearInterval, so
// dismiss() bumps this token to abandon the in-flight run instead (see
// runMiniAppBuild / runTranslateBuild below). Shared across both local modes since
// only one build can run at a time (see the guard at the top of start()).
let miniRunToken = 0;

function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

async function keepAwakeOn() {
  try {
    const KeepAwake = await import('expo-keep-awake');
    await KeepAwake.activateKeepAwakeAsync();
  } catch {
    /* keep-awake unavailable — silent */
  }
}

function keepAwakeOff() {
  void (async () => {
    try {
      const KeepAwake = await import('expo-keep-awake');
      /*
       * Awaited, because on the web this is asynchronous and the try/catch around it was
       * not catching anything.
       *
       * The browser's wake lock needs a visible document, so activation quietly fails
       * whenever the tab is in the background — and releasing a lock that was never taken
       * rejects. A build runs for the better part of a minute, which is exactly long
       * enough for someone to switch tabs, and the rejection surfaced as an uncaught
       * error in the middle of their build. The typings say this returns void on native,
       * where awaiting it costs nothing.
       */
      await KeepAwake.deactivateKeepAwake();
    } catch {
      /* keep-awake unavailable, or was never held — either way, nothing to do */
    }
  })();
}

function beginPolling(
  buildId: string,
  appName: string,
  goal: string,
  signedIn: boolean,
  set: (s: Partial<BuildRunState>) => void,
) {
  stopPolling();

  const poll = async () => {
    try {
      const resp = await getBuild(buildId, signedIn);
      if (resp.status === 'queued' || resp.status === 'running') {
        set({ phase: resp.phase ?? resp.status });
        return;
      }
      if (resp.status === 'done') {
        stopPolling();
        keepAwakeOff();
        mmkv.delete(MMKV_KEY);
        try {
          const { projectId } = await finalizeBuild(appName, goal, resp.definition!, resp.summary ?? '');
          track('generation_completed', { firstBuild: buildFirstBuild, ms: Date.now() - buildStartedAt });
          set({ status: 'idle', plan: null, phase: 'queued' });
          // No dismissAll() here — see the note above `enterApp` in src/app/_layout.tsx.
          // `replace()` targets a different top-level group (whatever the user was in →
          // "(main)"), so it swaps in a brand-new route with a fresh key and discards the
          // entire previous subtree by construction; a preceding dismissAll() had nothing
          // left to usefully do and only produced the "POP_TO_TOP…not handled" warning.
          router.replace(`/(main)/project/${projectId}?mode=preview` as any);
        } catch (finalizeErr) {
          track('generation_failed', { reason: 'finalize_failed' });
          set({ status: 'error', error: finalizeErr instanceof Error ? finalizeErr.message : 'Failed to finalize build.' });
        }
        return;
      }
      if (resp.status === 'error') {
        stopPolling();
        keepAwakeOff();
        mmkv.delete(MMKV_KEY);
        track('generation_failed', { reason: 'build_failed' });
        set({ status: 'error', error: resp.error ?? 'Build failed.' });
      }
    } catch {
      // Network/transient error during polling — keep polling, don't abort
    }
  };

  void poll();
  pollTimer = setInterval(poll, POLL_MS);
}





/**
 * THE build path. Saddle generates a mobile-native WEB app: the model writes the app
 * body, we assemble the document around it, the gate parses it, and publishing returns a
 * public URL — the bookmark, which is the actual product.
 *
 * Runs fully in-process like the mini-app path (the gate is a local pure function, so
 * unlike that path there is no compile service to be unreachable), while driving the same
 * status/phase/error state so the progress overlay cannot tell the modes apart.
 */
async function runWebAppBuild(
  inputs: { appName: string; goal: string; vibe?: string | null; plan?: AppPlan | null },
  signedIn: boolean,
  set: (s: Partial<BuildRunState>) => void,
) {
  const token = ++miniRunToken; // shares the abandon-token with the other in-process path
  const live = () => token === miniRunToken;

  set({ phase: 'generating' });
  await keepAwakeOn();

  try {
    const [{ publishViaBackend }, { buildWebAppProject }] = await Promise.all([
      import('@/webapp/publishClient'),
      import('@/onboarding/runWebAppBuild'),
    ]);

    // Generation happens on the server now; only the upload is still ours to do.
    const outcome = await buildWebAppProject(
      { appName: inputs.appName, goal: inputs.goal, signedIn, vibe: inputs.vibe, plan: inputs.plan },
      { publish: (html, appId) => publishViaBackend(html, appId) },
    );
    if (!live()) return; // dismissed mid-build

    set({ phase: 'done' });
    const { projectId } = await finalizeBuild(inputs.appName, inputs.goal, outcome.definition, outcome.summary);
    if (!live()) return;

    // Upgrade the bookmark to Saddle's own domain — app.saddle-sites.com/site/<name> —
    // which is the product promise and also a STABLE url (the upload endpoint mints a new
    // key per publish, so the direct link changes on every re-publish while the slug does
    // not). Runs after finalizeBuild because hosting needs the app in the catalog, and it
    // returns null rather than throwing: the app is already built, saved and reachable at
    // its direct link, so a hosting failure must never cost the user the build.
    if (outcome.url) {
      const { hostOnSaddle } = await import('@/webapp/saddleHost');
      const hosted = await hostOnSaddle(
        { id: projectId, name: inputs.appName, description: inputs.goal, definition: outcome.definition } as never,
        outcome.html,
      );
      if (hosted && live()) {
        await useProjectStore.getState().updateProject?.(projectId, {
          definition: { ...outcome.definition, webUrl: hosted.url, hostedAppId: hosted.appId },
        });
      } else if (live()) {
        /*
         * Hosting failed, so the app has no address yet — and the direct upload link is
         * not one.
         *
         * The comment above used to say the app was "reachable at its direct link", and
         * for a purely local app it is. For any app that lists or collects it is not: both
         * are same-origin calls to /apps/<id>/…, which only route behind Saddle's own
         * domain. Served from storage they return nothing, the app falls back to the rows
         * in its own source, and a shift board renders every date as "Invalid Date" — a
         * broken page handed over as a finished one.
         *
         * Better to say it is not published than to hand out a link that looks live and
         * is not. The build is saved either way; Republish is the way back.
         */
        await useProjectStore.getState().updateProject?.(projectId, {
          definition: { ...outcome.definition, webUrl: undefined },
        });
      }
    }

    // Holds the wizard state until success for the same reason the mini-app path does:
    // retry() re-reads buildInputsFromStore, which reset() wipes.
    useOnboardingStore.getState().reset();
    keepAwakeOff();
    track('generation_completed', {
      firstBuild: buildFirstBuild,
      ms: Date.now() - buildStartedAt,
      compileFailures: outcome.gateFailures,
    });
    set({ status: 'idle', plan: null, phase: 'queued' });
    router.replace(`/(main)/project/${projectId}?mode=preview` as any);
  } catch (e) {
    keepAwakeOff();
    if (!live()) return;
    const handled = routeDenied(e, {
      paywall: () => router.push('/(onboarding)/paywall' as any),
      signup: () => router.push('/(auth)/sign-up' as any),
      toast: (m) => set({ status: 'error', error: m }),
    });
    const isGateFailure = e instanceof WebAppGenerationError;
    track('generation_failed', {
      reason: isGateFailure ? 'webapp_gate_failed' : 'webapp_build_error',
      compileFailures: isGateFailure ? e.gateFailures : undefined,
    });
    if (!handled) {
      set({
        status: 'error',
        error: isGateFailure
          ? `The app didn't come out right after a few tries.\n\n${e.diagnostics.join('\n')}`
          : e instanceof Error ? e.message : 'Build failed. Please try again.',
      });
    } else {
      set({ status: 'idle', plan: null, phase: 'queued' });
    }
  }
}

export const useBuildRunStore = create<BuildRunState>((set, get) => ({
  status: 'idle',
  phase: 'queued',
  plan: null,
  error: null,

  start: (signedIn) => {
    if (get().status === 'running') return; // one build at a time
    lastSignedIn = signedIn;
    const inputs = buildInputsFromStore(signedIn);
    // THE fork between the three generation modes, and it must be read here — before any
    // path calls reset(), which restores the default 'engine' and would silently turn
    // every "Native (experimental)" build back into a standard engine build.
    const runtime = useOnboardingStore.getState().runtime;
    const plan = useOnboardingStore.getState().selectedPlan;
    // The guided front door's plan. Read here for the same reason `runtime` is: reset()
    // wipes it, and several paths below call reset() before the build reads anything.
    const appPlan = useOnboardingStore.getState().appPlan;
    buildFirstBuild = useProjectStore.getState().projects.length === 0;
    buildStartedAt = Date.now();
    set({ status: 'running', phase: 'queued', plan, error: null });
    track('generation_started', { firstBuild: buildFirstBuild });

    if (runtime === 'webapp') {
      void runWebAppBuild({ ...inputs, plan: appPlan }, signedIn, set);
      return;
    }
    // 'miniapp' and 'translate' are gone: that path generated React Native source and
    // ran it through `new Function(...)`, which App Review 2.5.2 forbids and which is why
    // the product publishes web pages instead. Nothing has selected them since.

    const buildId = nanoid();

    void (async () => {
      try {
        await createBuild(buildId, inputs, signedIn);
        mmkv.setObject(MMKV_KEY, { buildId, appName: inputs.appName, goal: inputs.goal, plan, signedIn } satisfies ActiveBuild);
        useOnboardingStore.getState().reset();
        await keepAwakeOn();
        beginPolling(buildId, inputs.appName, inputs.goal, signedIn, set);
      } catch (e) {
        const handled = routeDenied(e, {
          paywall: () => router.push('/(onboarding)/paywall' as any),
          signup: () => router.push('/(auth)/sign-up' as any),
          toast: (m) => set({ status: 'error', error: m }),
        });
        track('generation_failed', { reason: e instanceof Error && (e as { reason?: string }).reason ? String((e as { reason?: string }).reason) : 'build_error' });
        if (!handled) set({ status: 'error', error: e instanceof Error ? e.message : 'Build failed. Please try again.' });
        else if (get().status === 'running') set({ status: 'idle', plan: null, phase: 'queued' });
      }
    })();
  },

  resume: () => {
    const a = mmkv.getObject<ActiveBuild>(MMKV_KEY);
    if (!a || get().status === 'running') return;
    set({ status: 'running', phase: 'queued', plan: a.plan ?? null, error: null });
    void keepAwakeOn();
    beginPolling(a.buildId, a.appName, a.goal, a.signedIn, set);
  },

  retry: () => get().start(lastSignedIn),

  dismiss: () => {
    const wasError = get().status === 'error';
    if (get().status === 'running') track('generation_cancelled');
    miniRunToken++; // abandon an in-flight mini-app build (no timer to clear)
    stopPolling();
    keepAwakeOff();
    mmkv.delete(MMKV_KEY);
    set({ status: 'idle', error: null, phase: 'queued', plan: null });
    // `start()` is only ever called from the onboarding wizard (create.tsx /
    // choose-layout.tsx), and those screens have no back/exit control of their
    // own — they rely entirely on this store's post-build navigation to leave.
    // A successful build gets there via the replace() calls above; a FAILED one
    // only reaches BuildProgressOverlay's error screen, whose one button is this
    // dismiss(). Without an explicit landing spot here, dismissing a failure left
    // the user stranded on the wizard form with no way back to Saddle — the
    // literal "generated form with no back affordance" report. Only on an error
    // (not a live-build cancel, which existing behavior deliberately leaves
    // exactly where the user was) — replace() targets a route the user can
    // always leave from.
    if (wasError) {
      try { router.replace('/(main)/(tabs)' as any); } catch { /* navigator not mounted yet — nothing more we can do */ }
    }
  },
}));
